import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SUPPORTED_SEND_CURRENCIES, type SendCurrency } from '@monigo/shared';
import { Select } from '@monigo/ui';
import { Logo } from '../../components/Brand/Logo.js';
import { LanguageToggle } from '../../components/LanguageToggle.js';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/queryKeys.js';

interface RateRow {
  from_currency: string;
  to_currency: string;
  rate: number;
  updated_at: string;
}

interface QuoteResponse {
  rate: number;
  fee: number;
  amount_send: number;
  amount_receive: number;
  has_dynamic_tiers?: boolean;
}

const FALLBACK_RECEIVE_CURRENCY = 'XAF';

export function LandingPage() {
  const { t } = useTranslation();
  const [sendAmount, setSendAmount] = useState('');
  const [receiveAmountStr, setReceiveAmountStr] = useState('');
  const [activeField, setActiveField] = useState<'send' | 'receive'>('send');
  const [sendCurrency, setSendCurrency] = useState<SendCurrency>('EUR');
  const [receiveCurrency, setReceiveCurrency] = useState(FALLBACK_RECEIVE_CURRENCY);

  const ratesQ = useQuery({
    queryKey: qk.rates,
    queryFn: () => callFunction<{ rates: RateRow[] }>('rates-list'),
  });

  const corridors = useMemo(() => {
    const rateRows = ratesQ.data?.rates ?? [];
    if (!rateRows.length) {
      return SUPPORTED_SEND_CURRENCIES.map((from) => ({ from, to: FALLBACK_RECEIVE_CURRENCY }));
    }

    return rateRows.map((rate) => ({
      from: rate.from_currency as SendCurrency,
      to: rate.to_currency,
    }));
  }, [ratesQ.data?.rates]);

  useEffect(() => {
    const activeCorridor = corridors.find((corridor) => corridor.from === sendCurrency);
    if (activeCorridor && activeCorridor.to !== receiveCurrency) {
      setReceiveCurrency(activeCorridor.to);
    }
  }, [corridors, receiveCurrency, sendCurrency]);

  // Get the static rate (no fee) for display and inverse calculation
  const staticRate =
    ratesQ.data?.rates.find(
      (rate) => rate.from_currency === sendCurrency && rate.to_currency === receiveCurrency,
    )?.rate ?? 0;

  // Sync send amount when receive field is edited (approximate inverse)
  const effectiveSendAmount =
    activeField === 'send'
      ? sendAmount
      : receiveAmountStr && staticRate > 0
        ? String(Math.round((Number(receiveAmountStr) / staticRate) * 100) / 100)
        : sendAmount;

  const numericAmount = Number(effectiveSendAmount || '0');

  const quoteQ = useQuery({
    queryKey: ['public-quote', sendCurrency, receiveCurrency, numericAmount],
    queryFn: () =>
      callFunction<QuoteResponse>('rates-quote', {
        body: { from_currency: sendCurrency, to_currency: receiveCurrency, amount: numericAmount },
      }),
    enabled: Number.isFinite(numericAmount) && numericAmount > 0,
  });

  const currentRate = quoteQ.data?.rate ?? staticRate;

  const fee = quoteQ.data?.fee ?? 0;

  // Minimum send amount: equivalent of 10€
  const MIN_SEND_EUR = 10;
  const minSendAmount = MIN_SEND_EUR;
  const isBelowMinimum =
    Number(effectiveSendAmount) > 0 && Number(effectiveSendAmount) < minSendAmount;

  // Display values based on active field
  const displayedSendAmount = activeField === 'send' ? sendAmount : effectiveSendAmount;
  const displayedReceiveAmount =
    activeField === 'receive'
      ? receiveAmountStr
      : quoteQ.data?.amount_receive != null
        ? formatNumber(quoteQ.data.amount_receive, 0)
        : '';

  const handleReceiveAmountChange = (value: string) => {
    setActiveField('receive');
    setReceiveAmountStr(value);
  };

  const handleSendAmountChange = (value: string) => {
    setActiveField('send');
    setSendAmount(value);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-primary text-primary-foreground">
      <div className="relative isolate min-h-screen">
        <div className="absolute inset-x-0 top-0 h-[58vh] bg-[radial-gradient(circle_at_top,hsl(var(--color-success)/0.9),transparent_62%)] opacity-75" />
        <div className="absolute inset-x-0 bottom-0 h-[44vh] bg-[linear-gradient(180deg,hsl(var(--color-primary)/0)_0%,hsl(var(--color-primary))_35%)]" />

        <main className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 pb-12 pt-6 sm:px-8 lg:px-10">
          <header className="flex items-center justify-between">
            <Logo white />
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-3">
                <Link
                  to="/login"
                  className="rounded-pill border border-white/40 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  {t('auth.loginCta')}
                </Link>
                <Link
                  to="/signup"
                  className="rounded-pill bg-success px-5 py-2.5 text-sm font-semibold text-primary transition hover:opacity-95"
                >
                  {t('auth.signupCta')}
                </Link>
              </div>
              <LanguageToggle />
            </div>
          </header>

          <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center py-10 sm:py-14">
            <section className="mt-8 rounded-[2rem] bg-white p-5 text-text shadow-[0_28px_90px_rgba(0,0,0,0.16)] sm:p-7">
              <div className="space-y-6">
                <AmountField
                  label={t('transfers.youSend')}
                  amount={displayedSendAmount}
                  currency={sendCurrency}
                  onAmountChange={handleSendAmountChange}
                  onCurrencyChange={(value) => setSendCurrency(value as SendCurrency)}
                  options={corridors.map((corridor) => corridor.from)}
                  badgeByCurrency={{ EUR: '🇪🇺', USD: '🇺🇸', CAD: '🇨🇦' }}
                />

                <AmountField
                  label={t('transfers.theyReceive')}
                  amount={displayedReceiveAmount}
                  currency={receiveCurrency}
                  onAmountChange={handleReceiveAmountChange}
                  options={[receiveCurrency]}
                  badgeByCurrency={{ XAF: '🇨🇲' }}
                  staticCurrency
                />
              </div>

              <div className="mt-8 space-y-3 text-center">
                <p className="text-base text-text-muted sm:text-lg">
                  {t('landing.exchangeRate')}{' '}
                  <span className="font-semibold text-primary">
                    1 {sendCurrency} = {formatNumber(currentRate, 0)} {receiveCurrency}
                  </span>
                </p>
                <p className="text-base text-text-muted sm:text-lg">
                  {t('transfers.fee')}{' '}
                  <span className="font-semibold text-text">
                    {formatNumber(fee, 2)} {sendCurrency}
                  </span>
                </p>
                {isBelowMinimum ? (
                  <p className="text-sm text-danger">
                    {t('transfers.minimumAmount', { amount: minSendAmount, currency: 'EUR' })}
                  </p>
                ) : null}
                {quoteQ.data?.has_dynamic_tiers ? (
                  <div className="mt-3 text-sm text-text-muted">
                    <p className="mt-1 text-xs text-primary/70">
                      <span className="inline-block rounded-pill bg-primary/10 px-2 py-0.5 font-medium">
                        ⓘ {t('landing.estimateHintTiered')}
                      </span>
                    </p>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="mt-6 rounded-[2rem] bg-white p-5 text-text shadow-[0_20px_70px_rgba(0,0,0,0.14)] sm:p-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Link
                  to="/login"
                  className="flex h-14 items-center justify-center rounded-pill border border-success/55 text-lg font-semibold text-primary transition hover:bg-success/10"
                >
                  {t('auth.loginCta')}
                </Link>
                <Link
                  to="/signup"
                  className="flex h-14 items-center justify-center rounded-pill bg-success text-lg font-semibold text-primary transition hover:opacity-95"
                >
                  {t('auth.signupCta')}
                </Link>
              </div>
              <p className="mt-4 text-center text-sm text-text-muted">{t('landing.ctaHint')}</p>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function AmountField({
  label,
  amount,
  currency,
  onAmountChange,
  onCurrencyChange,
  options,
  badgeByCurrency,
  readOnly,
  staticCurrency,
}: {
  label: string;
  amount: string;
  currency: string;
  onAmountChange?: (value: string) => void;
  onCurrencyChange?: (value: string) => void;
  options: string[];
  badgeByCurrency: Record<string, string>;
  readOnly?: boolean;
  staticCurrency?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-base text-text-muted">{label}</p>
        <div className="flex items-center gap-2 text-lg font-semibold text-text">
          <span aria-hidden="true" className="inline-flex text-base leading-none">
            {badgeByCurrency[currency] ?? '💱'}
          </span>
          {staticCurrency || readOnly ? (
            <span className="pr-1">{currency}</span>
          ) : (
            <Select
              value={currency}
              onChange={(value) => onCurrencyChange?.(value)}
              options={
                options.length > 1
                  ? options.map((o) => ({ value: o, label: o }))
                  : [
                      { value: 'EUR', label: 'EUR' },
                      { value: 'USD', label: 'USD' },
                      { value: 'CAD', label: 'CAD' },
                    ]
              }
              className="min-w-24"
            />
          )}
        </div>
      </div>
      <div className="mt-3 border-b border-border pb-3">
        <input
          value={amount}
          readOnly={readOnly}
          onChange={(event) => onAmountChange?.(event.target.value.replace(/[^\d.]/g, ''))}
          placeholder="0.00"
          className="w-full bg-transparent text-4xl font-extrabold tracking-tight text-text outline-none placeholder:text-text-muted/40 sm:text-5xl"
          inputMode="decimal"
        />
      </div>
    </div>
  );
}

function formatNumber(value: number, digits: number) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}
